import { FC, useMemo } from 'react';
import { useNumericParam } from '../../common/useEntityIdParam';
import { Forbidden } from '../Forbidden';
import { NotFound } from '../NotFound';
import {
  PermissionCode,
  Spinner,
  useGetClassifierMappingQuery,
  useHasPermission,
} from '@atlas-frontend/sdk';
import { ClassifierMappingForm } from './ClassifierMappingForm';

interface EditModeProps {
  mode: 'clone' | 'edit';
}
interface InnerEditProps extends EditModeProps {
  id: number;
}

export const ClassifierMappingEdit: FC<EditModeProps> = ({ mode }) => {
  const id = useNumericParam('id');
  const hasPermission = useHasPermission(PermissionCode.CLASSIFIER_MANAGE);

  if (!id) {
    return <NotFound />;
  }

  return (
    <Forbidden isAllowed={hasPermission}>
      <ClassifierMappingEditInner id={id} mode={mode} />
    </Forbidden>
  );
};

const ClassifierMappingEditInner: FC<InnerEditProps> = ({ id, mode }) => {
  const { data, isLoading, refetch } = useGetClassifierMappingQuery(id);

  const editData = useMemo(
    () =>
      data && mode === 'clone'
        ? {
            ...data,
            id: undefined!,
            code: '',
            categoryMappings: data.categoryMappings.map((mapping) => ({
              ...mapping,
              id: undefined,
            })),
          }
        : data,
    [data, mode],
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!editData) {
    return null;
  }

  return (
    <ClassifierMappingForm
      key={editData.modifiedAt}
      data={editData}
      mode={mode}
      onSuccess={refetch}
    />
  );
};
